<template>
  <section>
    <nav-component :titulo="'Cierres de Caja'" />
    <b-breadcrumb align="is-left">
      <b-breadcrumb-item tag='router-link' to="/">Inicio</b-breadcrumb-item>
      <b-breadcrumb-item active>Cierres de Caja</b-breadcrumb-item>
    </b-breadcrumb>

    <!-- Filtros -->
    <div class="box">
      <div class="columns is-multiline">
        <div class="column is-3">
          <b-field label="Rango de fechas">
            <b-datepicker v-model="rangoFechas" range placeholder="Seleccione rango de fechas" :date-formatter="formatearRango" />
          </b-field>
        </div>
        <div class="column is-3">
          <b-field label="Usuario">
            <b-select v-model="usuarioId" placeholder="Todos los usuarios" clearable>
              <option :value="null">Todos</option>
              <option v-for="u in usuarios" :key="u.id" :value="u.id">{{ u.usuario }} - {{ u.nombre }}</option>
            </b-select>
          </b-field>
        </div>
        <div class="column is-3">
          <b-field label="Negocio">
            <b-select v-model="negocioId" placeholder="Todos los negocios" clearable @input="obtenerCierres">
              <option :value="null">Todos</option>
              <option v-for="n in negocios" :key="n.id" :value="n.id">{{ n.nombre }}</option>
            </b-select>
          </b-field>
        </div>
        <div class="column is-3 is-flex is-align-items-flex-end">
          <b-button type="is-primary" @click="obtenerCierres" :loading="cargando">
            <b-icon icon="magnify" />
            <span>Consultar</span>
          </b-button>
          <b-button type="is-info" class="ml-2" @click="btnImprimir" :disabled="!filas.length">
            <b-icon icon="printer" />
            <span>Imprimir</span>
          </b-button>
        </div>
      </div>
    </div>

    <!-- Resumen por almacén -->
    <div v-if="resumenAlmacenes.length > 0" class="box">
      <h3 class="title is-5">Resumen por almacén</h3>
      <div class="columns is-multiline">
        <div v-for="item in resumenAlmacenes" :key="item.id" class="column is-3">
          <div class="notification is-light" :style="{ borderLeft: '5px solid ' + item.color }">
            <p class="heading">{{ item.nombre }} ({{ item.cod }})</p>
            <p class="title is-4">${{ formatoMonto(item.total) }}</p>
          </div>
        </div>
        <div class="column is-3">
          <div class="notification is-dark">
            <p class="heading">TOTAL GENERAL</p>
            <p class="title is-4">${{ formatoMonto(totalGeneral) }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Selector visual: Ver por almacén (siempre visible si hay almacenes) -->
    <div v-if="almacenes.length > 0 && filas.length > 0" class="box">
      <div class="field is-grouped">
        <div class="control">
          <b-radio v-model="almacenFiltro" :native-value="null" @input="almacenFiltro = null">
            Todos
          </b-radio>
        </div>
        <div class="control" v-for="a in almacenes" :key="a.id">
          <b-radio v-model="almacenFiltro" :native-value="a.id">
            <span :style="{ color: a.color }">■</span> {{ a.nombre }}
          </b-radio>
        </div>
      </div>
    </div>

    <!-- Tabla detallada -->
    <div v-if="filasConDistribucion.length > 0" class="box">
      <h3 class="title is-5">Detalle de transacciones</h3>

      <b-table :data="filasConDistribucion" hoverable striped>
        <b-table-column field="id_referencia" label="N° Factura" sortable v-slot="props">
          {{ props.row.id_referencia }}
        </b-table-column>
        <b-table-column field="cliente" label="Cliente" sortable searchable v-slot="props">
          {{ props.row.cliente }}
        </b-table-column>
        <b-table-column label="Monto (almacén seleccionado)" numeric sortable v-slot="props">
          <b>${{ formatoMonto(montoMostrar(props.row)) }}</b>
        </b-table-column>
        <b-table-column label="Distribución por almacén" v-slot="props">
          <div v-if="obtenerDistribucion(props.row)" class="is-size-7">
            <span v-for="(monto, idAlmacen) in distribucionVisibilidad(props.row)" :key="idAlmacen"
                  class="mr-2" :style="{ color: colorAlmacen(idAlmacen) }">
              {{ nombreAlmacen(idAlmacen) }}: ${{ formatoMonto(monto) }}
            </span>
            <span v-if="!Object.keys(distribucionVisibilidad(props.row) || {}).length" class="has-text-grey">Sin distribuir</span>
          </div>
          <span v-else class="has-text-grey is-size-7">Sin distribuir</span>
        </b-table-column>
        <b-table-column field="metodo_pago" label="Método de pago" sortable v-slot="props">
          <div class="tags are-small">
            <b-tag v-for="m in String(props.row.desglose_pagos || '').split(' + ')" :key="m" :type="tagMetodo(m.split(':')[0].trim())" class="mr-1">
              {{ m.trim() }}
            </b-tag>
          </div>
        </b-table-column>
        <b-table-column label="Desglose" v-slot="props">
          <span class="is-size-7 has-text-grey">{{ props.row.desglose_pagos }}</span>
        </b-table-column>
        <b-table-column field="tipo" label="Tipo" sortable v-slot="props">
          <b-tag :type="props.row.tipo === 'VENTA' ? 'is-success' : 'is-warning'">
            {{ props.row.tipo }}
          </b-tag>
        </b-table-column>
        <b-table-column label="Acciones" v-slot="props">
          <b-button type="is-small" @click="abrirDistribucion(props.row)">
            <b-icon icon="swap-horizontal-bold" size="is-small" />
            <span>Distribuir</span>
          </b-button>
        </b-table-column>
      </b-table>
      <p v-if="hayDistribuciones" class="has-text-info is-size-7 mt-2">
        * Distribuciones locales. No afectan los datos originales.
      </p>
    </div>

    <mensaje-inicial v-if="!cargando && filas.length < 1 && consultado" :titulo="'No se encontraron transacciones'"
      :subtitulo="'Intente con otro rango de fechas'" />

    <!-- Modal distribucion -->
    <b-modal v-model="modalDistribucionActivo" :width="500" scroll="keep">
      <div class="box">
        <h3 class="title is-5">Distribuir por almacén</h3>
        <p class="subtitle is-6" v-if="filaDistribucion">
          Referencia #{{ filaDistribucion.id_referencia }} — 
          <b>${{ formatoMonto(filaDistribucion.monto_total) }}</b>
        </p>

        <div v-for="a in almacenes" :key="a.id" class="field">
          <b-field :label="a.nombre + ' (' + a.cod + ')'">
            <b-input
              :type="'number'"
              :step="'0.01'"
              :min="0"
                :max="filaDistribucion ? filaDistribucion.monto_total : 0"
              v-model="distribucionTemp[a.id]"
              @input="recalcularTotalDistribucion"
            />
          </b-field>
        </div>

        <div class="level">
          <div class="level-left">
            <b :class="totalDistribucionValido ? 'has-text-success' : 'has-text-danger'">
              Total distribuido: ${{ formatoMonto(totalDistribucionCalculado) }}
              <span v-if="!totalDistribucionValido"> (debe ser ${{ formatoMonto(filaDistribucion ? filaDistribucion.monto_total : 0) }})</span>
            </b>
          </div>
        </div>

        <div class="buttons is-right">
          <b-button @click="modalDistribucionActivo = false">Cancelar</b-button>
          <b-button type="is-primary" @click="guardarDistribucion" :disabled="!totalDistribucionValido">
            Guardar distribución
          </b-button>
        </div>
      </div>
    </b-modal>

    <b-loading :is-full-page="true" v-model="cargando" :can-cancel="false" />
  </section>
</template>

<script>
import HttpService from '../../Servicios/HttpService'
import NavComponent from '../Extras/NavComponent'
import MensajeInicial from '../Extras/MensajeInicial'
import Utiles from '../../Servicios/Utiles'

export default {
  name: 'CierresComponent',
  components: { NavComponent, MensajeInicial },

  data: () => ({
    fechaInicio: new Date(),
    fechaFin: new Date(),
    usuarioId: null,
    negocioId: null,
    usuarios: [],
    negocios: [],
    almacenes: [],
    filas: [],
    cargando: false,
    consultado: false,
    almacenFiltro: null,

    // Distribucion local (temporal, no afecta BD)
    distribucionAlmacenes: {}, // key: "venta_5" o "abono_12" → { idAlmacen: monto, ... }

    // Modal distribucion
    modalDistribucionActivo: false,
    filaDistribucion: null,
    distribucionTemp: {},
  }),

  mounted() {
    this.cargarUsuarios()
    this.cargarAlmacenes()
    this.cargarNegocios()
    this.obtenerCierres()
  },

  computed: {
    rangoFechas: {
      get() {
        return [this.fechaInicio, this.fechaFin]
      },
      set(valor) {
        if (Array.isArray(valor) && valor.length === 2 && valor[0] && valor[1]) {
          this.fechaInicio = valor[0]
          this.fechaFin = valor[1]
        }
      },
    },

    filasConDistribucion() {
      let filas = this.filas
      // Al seleccionar un almacén, solo mostrar transacciones con monto en ese almacén
      if (this.almacenFiltro) {
        filas = filas.filter(f => {
          const key = f.payment_type + '_' + f.id_referencia
          const dist = this.distribucionAlmacenes[key]
          return dist && dist[this.almacenFiltro] !== undefined && parseFloat(dist[this.almacenFiltro]) > 0
        })
      }
      return filas
    },

    resumenAlmacenes() {
      const totals = {}
      this.almacenes.forEach(a => {
        totals[a.id] = { id: a.id, nombre: a.nombre, cod: a.cod, color: a.color, total: 0 }
      })

      this.filas.forEach(f => {
        const key = f.payment_type + '_' + f.id_referencia
        const dist = this.distribucionAlmacenes[key]
        if (dist) {
          Object.entries(dist).forEach(([idAlmacen, monto]) => {
            if (totals[idAlmacen]) {
              totals[idAlmacen].total += parseFloat(monto || 0)
            }
          })
        }
      })

      return Object.values(totals).filter(a => a.total > 0)
        .sort((a, b) => a.nombre.localeCompare(b.nombre))
    },

    totalGeneral() {
      return this.resumenAlmacenes.reduce((s, i) => s + i.total, 0)
    },

    hayDistribuciones() {
      return Object.keys(this.distribucionAlmacenes).length > 0
    },

    totalDistribucionCalculado() {
      return Object.values(this.distribucionTemp).reduce((s, v) => s + (parseFloat(v) || 0), 0)
    },

    totalDistribucionValido() {
      const total = this.filaDistribucion?.monto_total || 0
      return Math.abs(this.totalDistribucionCalculado - total) < 0.01
    },
  },

  methods: {
    formatoMonto(valor) {
      return Utiles.formatoMonto(valor)
    },
    formatearFecha(fecha) {
      if (!fecha) return ''
      const d = new Date(fecha)
      return d.toLocaleDateString('es-VE', { year: 'numeric', month: '2-digit', day: '2-digit' })
    },

    formatearRango(fechas) {
      if (!Array.isArray(fechas) || fechas.length < 2) return ''
      if (!fechas[0] || !fechas[1]) return ''
      return `${this.formatearFecha(fechas[0])} - ${this.formatearFecha(fechas[1])}`
    },
    parsearFecha(fecha) {
      return new Date(fecha)
    },
    colorMetodo(metodo) {
      const colores = {
        'Efectivo ($)': 'is-success',
        'Punto de Venta': 'is-info',
        'Credito': 'is-danger',
        'Transferencia': 'is-link',
        'Pago Móvil': 'is-primary',
        'Zelle': 'is-warning',
        'Binance': 'is-dark',
      }
      return colores[metodo] || 'is-light'
    },
    tagMetodo(metodo) {
      const colores = {
        'Efectivo ($)': 'is-success',
        'Punto de Venta': 'is-info',
        'Credito': 'is-danger',
        'Transferencia': 'is-link',
        'Pago Móvil': 'is-primary',
        'Zelle': 'is-warning',
        'Binance': 'is-dark',
      }
      return colores[metodo] || 'is-light'
    },
    colorAlmacen(idAlmacen) {
      const a = this.almacenes.find(a => String(a.id) === String(idAlmacen))
      return a ? a.color : '#999'
    },
    nombreAlmacen(idAlmacen) {
      const a = this.almacenes.find(a => String(a.id) === String(idAlmacen))
      return a ? a.cod : String(idAlmacen)
    },

    obtenerDistribucion(row) {
      const key = row.payment_type + '_' + row.id_referencia
      return this.distribucionAlmacenes[key] || null
    },

    // Muestra solo el almacén seleccionado si hay filtro; todos si es "Todos"
    distribucionVisibilidad(row) {
      const dist = this.obtenerDistribucion(row)
      if (!dist) return null
      if (!this.almacenFiltro) return dist
      const solo = {}
      if (dist[this.almacenFiltro] !== undefined) {
        solo[this.almacenFiltro] = dist[this.almacenFiltro]
      }
      return solo
    },

    // Monto a mostrar según el almacén filtrado
    montoMostrar(row) {
      if (!this.almacenFiltro) return row.monto_total
      const dist = this.obtenerDistribucion(row)
      return dist && dist[this.almacenFiltro] !== undefined ? dist[this.almacenFiltro] : 0
    },

    abrirDistribucion(row) {
      this.filaDistribucion = row
      const key = row.payment_type + '_' + row.id_referencia
      const existente = this.distribucionAlmacenes[key] || {}

      // Inicializar temp con distribución existente o 0 para cada almacén
      this.distribucionTemp = {}
      this.almacenes.forEach(a => {
        this.distribucionTemp[a.id] = existente[a.id] || 0
      })

      this.distribucionTemp = { ...this.distribucionTemp }
      this.modalDistribucionActivo = true
    },

    recalcularTotalDistribucion() {
      this.$forceUpdate()
    },

    guardarDistribucion() {
      if (!this.totalDistribucionValido || !this.filaDistribucion) return

      const key = this.filaDistribucion.payment_type + '_' + this.filaDistribucion.id_referencia

      // Filtrar solo almacenes con monto > 0
      const dist = {}
      Object.entries(this.distribucionTemp).forEach(([idAlmacen, monto]) => {
        const m = parseFloat(monto)
        if (m > 0) dist[idAlmacen] = m
      })

      if (Object.keys(dist).length > 0) {
        this.$set(this.distribucionAlmacenes, key, dist)
      } else {
        this.$delete(this.distribucionAlmacenes, key)
      }

      this.distribucionAlmacenes = { ...this.distribucionAlmacenes }
      this.modalDistribucionActivo = false

      this.$buefy.toast.open({
        type: 'is-success',
        message: 'Distribución guardada (solo para este reporte)',
      })
    },

    async cargarUsuarios() {
      try {
        const res = await HttpService.obtenerConConsultas('usuarios.php', { accion: 'obtener' })
        this.usuarios = res || []
      } catch (e) {
        this.usuarios = []
      }
    },

    async cargarAlmacenes() {
      try {
        const res = await HttpService.obtenerConConsultas('almacenes.php', { accion: 'obtener' })
        this.almacenes = res || []
      } catch (e) {
        this.almacenes = []
      }
    },

    async cargarNegocios() {
      try {
        const res = await HttpService.obtenerConConsultas('negocios.php', { accion: 'obtener' })
        this.negocios = res || []
      } catch (e) {
        this.negocios = []
      }
    },

    async obtenerCierres() {
      this.cargando = true
      this.consultado = true

      const fmtFecha = (d) => {
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${y}-${m}-${day}`
      }

      const filtros = {
        fechaInicio: fmtFecha(this.fechaInicio),
        fechaFin: fmtFecha(this.fechaFin),
        idNegocio: this.negocioId,
      }

      try {
        const detalle = await HttpService.obtenerConConsultas('cierres.php', {
          accion: 'obtener',
          filtros,
          usuarioId: this.usuarioId,
        })
        this.filas = detalle || []
        // Parsear distribución automática desde el API
        this.distribucionAlmacenes = {}
        this.filas.forEach(f => {
          if (f.distribucion_almacenes) {
            const key = f.payment_type + '_' + f.id_referencia
            const dist = {}
            String(f.distribucion_almacenes).split('|').forEach(part => {
              const [idAlmacen, monto] = part.split(':')
              if (idAlmacen && monto) dist[idAlmacen] = parseFloat(monto)
            })
            if (Object.keys(dist).length > 0) {
              this.$set(this.distribucionAlmacenes, key, dist)
            }
          }
        })
        this.distribucionAlmacenes = { ...this.distribucionAlmacenes }
      } catch (e) {
        this.filas = []
      }

      this.cargando = false
    },

    btnImprimir() {
      const fmtFecha = (d) => {
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${y}-${m}-${day}`
      }

      const base = window.location.href.split('#')[0]
      const params = new URLSearchParams({
        fechaInicio: fmtFecha(this.fechaInicio),
        fechaFin: fmtFecha(this.fechaFin),
        usuarioId: this.usuarioId || '',
        negocioId: this.negocioId || '',
        almacenId: this.almacenFiltro || '',
        // Distribuciones manuales del usuario (temporales, no se guardan en BD)
        ajustes: JSON.stringify({ distribuciones: this.distribucionAlmacenes }),
      })

      window.open(`${base}#/pdf/cierres?${params.toString()}`, '_blank')
    },
  },
}
</script>